import { useState, useEffect } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import {
  getStudentByProfileId,
  getStudentStats,
  getUnitsWithProgress,
  getStudentLessonProgress,
  getLessonsWithProgress,
  getContentsByLesson,
  getStudentContentProgress,
  getExamAttempts,
  getStudentWorkshopAttendance,
  syncStudentProgress
} from '@/lib/db'
import type {
  Student,
  StudentStats,
  UnitWithProgress,
  StudentLessonProgress,
  LessonWithProgress,
  StudentContentProgress,
  Content,
  ExamAttempt,
  WorkshopAttendance
} from '@/types'
import { cn } from '@/lib/utils'
import {
  ArrowLeft,
  User,
  Star,
  Flame,
  BookOpen,
  Video,
  CheckCircle,
  PlayCircle,
  Lock,
  Mail,
  Loader2,
  FileText,
  ChevronDown,
  Headphones,
  PenTool,
  BookOpenCheck,
} from 'lucide-react'

export default function StudentDetail() {
  const { profileId } = useParams<{ profileId: string }>()
  const location = useLocation()
  // Si nos abrieron desde el panel admin, el "back" debe llevar a /admin/progress
  // (no al listado del profesor).
  const backHref = location.pathname.startsWith('/admin/') ? '/admin/progress' : '/teacher/students'
  const backLabel = location.pathname.startsWith('/admin/') ? 'Volver a avance' : 'Volver a estudiantes'

  const [student, setStudent] = useState<Student | null>(null)
  const [stats, setStats] = useState<StudentStats | null>(null)
  const [units, setUnits] = useState<UnitWithProgress[]>([])
  const [lessonProgress, setLessonProgress] = useState<StudentLessonProgress[]>([])
  const [examAttempts, setExamAttempts] = useState<ExamAttempt[]>([])
  const [attendance, setAttendance] = useState<WorkshopAttendance[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Accordion: unidad expandida
  const [expandedUnit, setExpandedUnit] = useState<string | null>(null)
  const [unitLessonsMap, setUnitLessonsMap] = useState<Record<string, LessonWithProgress[]>>({})
  const [lessonContentsMap, setLessonContentsMap] = useState<Record<string, Content[]>>({})
  const [contentProgressMap, setContentProgressMap] = useState<Record<string, StudentContentProgress>>({})
  const [loadingUnit, setLoadingUnit] = useState(false)

  useEffect(() => {
    if (profileId) loadStudentData()
  }, [profileId])

  const loadStudentData = async () => {
    setLoading(true)
    setError(null)

    try {
      // Paso 1: Resolver profile_id → student record
      const studentRes = await getStudentByProfileId(profileId!)
      if (studentRes.error || !studentRes.data) {
        setError('Estudiante no encontrado')
        setLoading(false)
        return
      }

      const studentData = studentRes.data
      setStudent(studentData)
      const studentId = studentData.id

      // Paso 2: Sync progress
      await syncStudentProgress(studentId).catch(() => {})

      // Paso 3: Fetch todo en paralelo. Pasamos el profile_id del alumno
      // a getUnitsWithProgress para que filtre cohortes por el alumno y NO
      // por el profesor que está consultando (auth.uid()).
      const [statsRes, unitsRes, lessonRes, examRes, attendanceRes] = await Promise.all([
        getStudentStats(studentId),
        getUnitsWithProgress(studentId, studentData.profile_id),
        getStudentLessonProgress(studentId),
        getExamAttempts(studentId),
        getStudentWorkshopAttendance(studentId)
      ])

      if (statsRes.data) setStats(statsRes.data)
      if (unitsRes.data) setUnits(unitsRes.data)
      if (lessonRes.data) setLessonProgress(lessonRes.data)
      if (examRes.data) setExamAttempts(examRes.data)
      if (attendanceRes.data) setAttendance(attendanceRes.data)
    } catch (err) {
      console.error('Error loading student data:', err)
      setError('Error al cargar datos')
    }

    setLoading(false)
  }

  const toggleUnit = async (unitId: string) => {
    if (expandedUnit === unitId) {
      setExpandedUnit(null)
      return
    }

    setExpandedUnit(unitId)

    // Si ya cargamos las lecciones de esta unidad, no recargar
    if (unitLessonsMap[unitId]) return

    setLoadingUnit(true)
    const studentId = student!.id

    // Paso 1: lecciones con progreso
    const lessonsRes = await getLessonsWithProgress(studentId, unitId)
    const lessons = lessonsRes.data || []
    setUnitLessonsMap(prev => ({ ...prev, [unitId]: lessons }))

    // Paso 2: contenidos por lección + progreso de contenidos
    const allContents: Content[] = []
    for (const lesson of lessons) {
      const contentsRes = await getContentsByLesson(lesson.id)
      const contents = contentsRes.data || []
      allContents.push(...contents)
      setLessonContentsMap(prev => ({ ...prev, [lesson.id]: contents }))
    }

    // Paso 3: progreso de contenidos (todos a la vez)
    if (allContents.length > 0) {
      const progressRes = await getStudentContentProgress(studentId)
      if (progressRes.data) {
        const map: Record<string, StudentContentProgress> = {}
        for (const cp of progressRes.data) {
          map[cp.content_id] = cp
        }
        setContentProgressMap(prev => ({ ...prev, ...map }))
      }
    }

    setLoadingUnit(false)
  }

  // Calcular progreso general
  const totalContents = units.length * 4 * 4 // 4 unidades × 4 lecciones × 4 contenidos
  const completedContents = lessonProgress.reduce((sum, lp) => sum + (lp.contents_completed || 0), 0)
  const progressPercent = totalContents > 0 ? Math.round((completedContents / totalContents) * 100) : 0

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('es-ES', {
      day: 'numeric', month: 'short', year: 'numeric'
    })
  }

  const formatTime = (seconds: number | null) => {
    if (!seconds) return '-'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error || !student) {
    return (
      <div className="text-center py-12">
        <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-lg font-medium text-muted-foreground">{error || 'Estudiante no encontrado'}</p>
        <Link to={backHref} className="text-primary hover:underline mt-2 inline-block">
          {`← ${backLabel}`}
        </Link>
      </div>
    )
  }

  const profile = student.profile

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={backHref}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">
            {profile?.first_name} {profile?.last_name}
          </h1>
          <p className="text-muted-foreground">{profile?.email}</p>
        </div>
        <a
          href={`mailto:${profile?.email}`}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Mail className="w-4 h-4" />
          Email
        </a>
      </div>

      {/* Perfil + Stats */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Info del perfil */}
        <div className="bg-card rounded-xl border p-6">
          <div className="text-center mb-4">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <User className="w-10 h-10 text-primary" />
              )}
            </div>
            <span className={cn(
              'px-3 py-1 rounded-full text-xs font-medium',
              student.subscription_status === 'active' ? 'bg-green-100 text-green-700' :
              student.subscription_status === 'trial' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-700'
            )}>
              {student.subscription_status === 'active' ? 'Premium' :
               student.subscription_status === 'trial' ? 'Trial' :
               student.subscription_status}
            </span>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Inscrito el</span>
              <span className="font-medium">{formatDate(student.enrollment_date)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Última actividad</span>
              <span className="font-medium">{formatDate(student.last_activity_date)}</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Idioma</span>
              <span className="font-medium">{profile?.preferred_language === 'fr' ? 'Français' : 'Español'}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Estado</span>
              <span className={cn(
                'px-2 py-0.5 rounded-full text-xs font-medium',
                profile?.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              )}>
                {profile?.is_active ? 'Activo' : 'Inactivo'}
              </span>
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="md:col-span-2 grid grid-cols-2 gap-4">
          <div className="bg-card rounded-xl border p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.totalPoints || 0}</p>
                <p className="text-xs text-muted-foreground">Puntos totales</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl border p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Flame className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.streakDays || 0}</p>
                <p className="text-xs text-muted-foreground">Días de racha</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl border p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{units.reduce((sum, u) => sum + (u.progress?.lessons_completed || 0), 0)}</p>
                <p className="text-xs text-muted-foreground">Lecciones completadas</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-xl border p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Video className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.workshopsAttended || 0}</p>
                <p className="text-xs text-muted-foreground">Talleres asistidos</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Barra de progreso general */}
      <div className="bg-card rounded-xl border p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-lg">Progreso general</h2>
          <span className="text-sm font-medium text-muted-foreground">{progressPercent}%</span>
        </div>
        <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          {completedContents} / {totalContents} contenidos completados
        </p>
      </div>

      {/* Progreso por unidad */}
      <div className="bg-card rounded-xl border p-6">
        <h2 className="font-semibold text-lg mb-4">Progreso por unidad</h2>
        <div className="space-y-4">
          {units.map((unit) => {
            const lessonsCompleted = unit.progress?.lessons_completed || 0
            const totalLessons = unit.progress?.total_lessons || 4
            const unitPercent = totalLessons > 0 ? Math.round((lessonsCompleted / totalLessons) * 100) : 0
            const isExpanded = expandedUnit === unit.id

            const StatusIcon = unit.progress?.status === 'completed' ? CheckCircle :
                              unit.progress?.status === 'in_progress' || unit.progress?.status === 'workshop_pending' || unit.progress?.status === 'exam_pending' ? PlayCircle :
                              Lock

            const statusColor = unit.progress?.status === 'completed' ? 'text-green-500' :
                               unit.progress?.status === 'in_progress' || unit.progress?.status === 'workshop_pending' || unit.progress?.status === 'exam_pending' ? 'text-blue-500' :
                               'text-gray-400'

            return (
              <div key={unit.id} className="border rounded-lg overflow-hidden">
                {/* Unit header - clickeable */}
                <button
                  onClick={() => toggleUnit(unit.id)}
                  className="w-full p-4 text-left hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <StatusIcon className={cn('w-5 h-5', statusColor)} />
                      <h3 className="font-semibold">
                        Unidad {unit.order_num} — {unit.title}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{unitPercent}%</span>
                      <ChevronDown className={cn(
                        'w-4 h-4 text-muted-foreground transition-transform',
                        isExpanded && 'rotate-180'
                      )} />
                    </div>
                  </div>

                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mt-3">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${unitPercent}%` }} />
                  </div>

                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
                    <span>{lessonsCompleted}/{totalLessons} lecciones</span>
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {unit.progress?.exam_passed ? (
                          <span className="text-green-600">Aprobado ({unit.progress.exam_score}%)</span>
                        ) : unit.progress?.exam_score != null ? (
                          <span className="text-red-500">Reprobado ({unit.progress.exam_score}%)</span>
                        ) : (
                          <span>Sin rendir</span>
                        )}
                      </span>
                      <span className="flex items-center gap-1">
                        <Video className="w-3 h-3" />
                        {unit.progress?.workshop_attended ? (
                          <span className="text-green-600">Asistió</span>
                        ) : (
                          <span>No asistió</span>
                        )}
                      </span>
                    </div>
                  </div>
                </button>

                {/* Contenido expandido: lecciones y contenidos */}
                {isExpanded && (
                  <div className="border-t bg-muted/20 p-4 space-y-3">
                    {loadingUnit && !unitLessonsMap[unit.id] ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="w-5 h-5 animate-spin text-primary" />
                      </div>
                    ) : (
                      (unitLessonsMap[unit.id] || []).map((lesson) => {
                        const lStatus = lesson.progress?.status
                        const LessonIcon = lStatus === 'completed' ? CheckCircle :
                                          lStatus === 'in_progress' ? PlayCircle : Lock
                        const lessonColor = lStatus === 'completed' ? 'text-green-500' :
                                           lStatus === 'in_progress' ? 'text-blue-500' : 'text-gray-400'
                        const contentsCompleted = lesson.progress?.contents_completed || 0
                        const totalContentsLesson = lesson.progress?.total_contents || (lessonContentsMap[lesson.id]?.length || 4)
                        const lessonPercent = totalContentsLesson > 0 ? Math.round((contentsCompleted / totalContentsLesson) * 100) : 0

                        return (
                          <div key={lesson.id} className="bg-card rounded-lg border p-3 space-y-2">
                            {/* Lesson header */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <LessonIcon className={cn('w-4 h-4', lessonColor)} />
                                <span className="font-medium text-sm">{lesson.title}</span>
                              </div>
                              <span className={cn(
                                'px-2 py-0.5 rounded-full text-[10px] font-medium',
                                lStatus === 'completed' ? 'bg-green-100 text-green-700' :
                                lStatus === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-500'
                              )}>
                                {lStatus === 'completed' ? 'Completada' :
                                 lStatus === 'in_progress' ? 'En progreso' :
                                 'Bloqueada'}
                              </span>
                            </div>

                            {/* Lesson progress bar */}
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-primary rounded-full" style={{ width: `${lessonPercent}%` }} />
                              </div>
                              <span className="text-[10px] text-muted-foreground w-16 text-right">
                                {contentsCompleted}/{totalContentsLesson} cont.
                              </span>
                            </div>

                            {/* Contents detail */}
                            {(lessonContentsMap[lesson.id] || []).length > 0 && (
                              <div className="pl-6 space-y-1.5 pt-1">
                                {(lessonContentsMap[lesson.id] || []).map((content, cIdx) => {
                                  const cp = contentProgressMap[content.id]
                                  // Inferir estado: si hay registro de progreso, usar su status.
                                  // Si no hay registro pero la lección está completa, todos son completed.
                                  // Si la lección está en progreso, los primeros N (contents_completed) están completados.
                                  let inferredStatus: string | undefined = cp?.status
                                  if (!inferredStatus) {
                                    if (lStatus === 'completed') {
                                      inferredStatus = 'completed'
                                    } else if (lStatus === 'in_progress' && cIdx < contentsCompleted) {
                                      inferredStatus = 'completed'
                                    } else if (lStatus === 'in_progress' && cIdx === contentsCompleted) {
                                      inferredStatus = 'in_progress'
                                    }
                                  }

                                  const CIcon = inferredStatus === 'completed' ? CheckCircle :
                                               inferredStatus === 'in_progress' ? PlayCircle : Lock
                                  const cColor = inferredStatus === 'completed' ? 'text-green-500' :
                                                inferredStatus === 'in_progress' ? 'text-blue-500' : 'text-gray-300'

                                  return (
                                    <div key={content.id} className="space-y-1">
                                      <div className="flex items-center gap-2 text-xs">
                                        <CIcon className={cn('w-3 h-3', cColor)} />
                                        <span className="flex-1 truncate">{content.title}</span>
                                        <span className={cn(
                                          'text-[10px]',
                                          inferredStatus === 'completed' ? 'text-green-600' :
                                          inferredStatus === 'in_progress' ? 'text-blue-600' : 'text-gray-400'
                                        )}>
                                          {inferredStatus === 'completed' ? 'Listo' :
                                           inferredStatus === 'in_progress' ? 'En curso' :
                                           '—'}
                                        </span>
                                      </div>
                                      {/* Skill breakdown si hay registro de progreso */}
                                      {cp && (inferredStatus === 'in_progress' || inferredStatus === 'completed') && (
                                        <div className="flex items-center gap-3 pl-5 text-[10px] text-muted-foreground">
                                          <span className="flex items-center gap-0.5" title="Escucha">
                                            <Headphones className="w-3 h-3 text-blue-400" /> {cp.listening_completed || 0}
                                          </span>
                                          <span className="flex items-center gap-0.5" title="Escritura">
                                            <PenTool className="w-3 h-3 text-green-400" /> {cp.writing_completed || 0}
                                          </span>
                                          <span className="flex items-center gap-0.5" title="Lectura">
                                            <BookOpenCheck className="w-3 h-3 text-orange-400" /> {cp.reading_completed || 0}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      })
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Resultados de exámenes */}
      <div className="bg-card rounded-xl border p-6">
        <h2 className="font-semibold text-lg mb-4">Resultados de exámenes</h2>
        {examAttempts.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground text-sm">Sin exámenes rendidos</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 font-medium text-muted-foreground">Tipo</th>
                  <th className="pb-2 font-medium text-muted-foreground">Puntaje</th>
                  <th className="pb-2 font-medium text-muted-foreground">Resultado</th>
                  <th className="pb-2 font-medium text-muted-foreground">Tiempo</th>
                  <th className="pb-2 font-medium text-muted-foreground">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {examAttempts.map((attempt) => (
                  <tr key={attempt.id} className="border-b last:border-0">
                    <td className="py-3">
                      <span className="capitalize">
                        {attempt.exam_type === 'unit'
                          ? 'Examen unidad'
                          : 'Examen final'}
                      </span>
                    </td>
                    <td className="py-3 font-semibold">{attempt.score}%</td>
                    <td className="py-3">
                      <span className={cn(
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        attempt.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      )}>
                        {attempt.passed ? 'Aprobado' : 'Reprobado'}
                      </span>
                    </td>
                    <td className="py-3 text-muted-foreground">{formatTime(attempt.time_taken_seconds)}</td>
                    <td className="py-3 text-muted-foreground">{formatDate(attempt.completed_at || attempt.started_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Asistencia a talleres */}
      <div className="bg-card rounded-xl border p-6">
        <h2 className="font-semibold text-lg mb-4">Asistencia a talleres</h2>
        {attendance.length === 0 ? (
          <div className="text-center py-8">
            <Video className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-muted-foreground text-sm">Sin asistencias registradas</p>
          </div>
        ) : (
          <div className="space-y-3">
            {attendance.map((att) => (
              <div key={att.id} className="flex items-center justify-between py-3 border-b last:border-0">
                <div className="flex items-center gap-3">
                  <Video className="w-5 h-5 text-purple-500" />
                  <div>
                    <p className="font-medium text-sm">
                      {att.workshop
                        ? ((att.workshop as any).title_es || att.workshop.title)
                        : 'Taller'}
                    </p>
                    {att.marked_at && (
                      <p className="text-xs text-muted-foreground">{formatDate(att.marked_at)}</p>
                    )}
                  </div>
                </div>
                <span className={cn(
                  'px-2 py-0.5 rounded-full text-xs font-medium',
                  att.status === 'attended' ? 'bg-green-100 text-green-700' :
                  att.status === 'absent' ? 'bg-red-100 text-red-700' :
                  'bg-yellow-100 text-yellow-700'
                )}>
                  {att.status === 'attended' ? 'Asistió' :
                   att.status === 'absent' ? 'Ausente' :
                   'Pendiente'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  )
}
